import { useGridCellEditor, type CustomCellEditorProps } from 'ag-grid-react';
import type { KeyboardEvent, MouseEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { SampleSheetRowDraft } from './sample-sheet-data';

type ListItem = { kind: 'option'; value: string; label: string } | { kind: 'create'; value: string; label: string };

type FreeTextDropdownEditorOptions = {
  options?: readonly string[];
  formatOption?: (value: string, row?: SampleSheetRowDraft) => string;
  placeholder?: string;
  caseInsensitive?: boolean;
  specialCreateLabel?: (typed: string) => string;
  onCommit?: (value: string) => string;
  resolveCanonicalValue?: (value: string) => string | null;
  createSectionLabel?: string;
  existingSectionLabel?: string;
};

export type FreeTextDropdownEditorParams = CustomCellEditorProps<SampleSheetRowDraft, string> &
  FreeTextDropdownEditorOptions;

export type FreeTextDropdownEditorConfig = FreeTextDropdownEditorOptions;

type SampleSheetGridContext = {
  sampleSheet?: {
    moveSelectionAfterEdit?: (
      columnId: string | null | undefined,
      rowIndex: number | null | undefined,
      delta: number,
    ) => void;
  };
};

function isString(value: unknown): value is string {
  return typeof value === 'string';
}

function normalizeForMatch(value: string, caseInsensitive: boolean): string {
  const base = value;
  return caseInsensitive ? base.toLowerCase() : base;
}

function findCaseInsensitiveMatch(value: string, options: readonly string[], caseInsensitive: boolean): string | null {
  if (!value) {
    return null;
  }
  const target = normalizeForMatch(value, caseInsensitive);
  for (const option of options) {
    if (normalizeForMatch(option, caseInsensitive) === target) {
      return option;
    }
  }
  return null;
}

export function FreeTextDropdownEditor(params: FreeTextDropdownEditorParams) {
  const {
    options: rawOptions,
    formatOption,
    placeholder,
    caseInsensitive = true,
    specialCreateLabel,
    onCommit,
    resolveCanonicalValue,
    createSectionLabel,
    existingSectionLabel,
    onValueChange,
    stopEditing,
    parseValue,
    onKeyDown,
    api,
  } = params;

  const options = useMemo<readonly string[]>(() => rawOptions ?? [], [rawOptions]);
  const rowData = params.data;
  const initialInput = useMemo(() => {
    const key = params.eventKey;
    if (key && key.length === 1) {
      return key;
    }
    const currentValue: unknown = params.value;
    if (currentValue == null) {
      return '';
    }
    const stringValue = isString(currentValue) ? currentValue : String(currentValue);
    if (formatOption) {
      const formatted = formatOption(stringValue, rowData);
      return formatted.length > 0 ? formatted : stringValue;
    }
    return stringValue;
  }, [formatOption, params.eventKey, params.value, rowData]);

  const [inputValue, setInputValue] = useState<string>(initialInput);
  const trimmedInput = useMemo(() => inputValue.trim(), [inputValue]);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const committedValueRef = useRef<string | null>(null);
  const interactedRef = useRef<boolean>(params.eventKey != null && params.eventKey.length === 1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const latestInputValueRef = useRef(inputValue);
  const initialInputRef = useRef(initialInput);
  // Track whether an activeIndex change should auto-scroll the list.
  const shouldAutoScrollRef = useRef(false);

  useEffect(() => {
    latestInputValueRef.current = inputValue;
  }, [inputValue]);

  useEffect(() => {
    initialInputRef.current = initialInput;
  }, [initialInput]);

  useEffect(() => {
    const inputElement = inputRef.current;
    if (inputElement) {
      inputElement.focus();
      const valueLength = inputElement.value.length;
      inputElement.setSelectionRange(valueLength, valueLength);
    }
  }, []);

  const formattedOptions = useMemo<readonly string[]>(() => {
    return options.map(option => {
      const label = formatOption ? formatOption(option, rowData) : option;
      return label.length > 0 ? label : option;
    });
  }, [formatOption, options, rowData]);

  const getCanonicalValue = useCallback(
    (value: string) => {
      if (!value) {
        return null;
      }
      if (resolveCanonicalValue) {
        return resolveCanonicalValue(value);
      }
      return findCaseInsensitiveMatch(value, options, caseInsensitive);
    },
    [caseInsensitive, options, resolveCanonicalValue],
  );

  const filteredOptions = useMemo<readonly string[]>(() => {
    const normalizedInput = normalizeForMatch(trimmedInput, caseInsensitive);
    if (normalizedInput === '') {
      return options;
    }
    return options.filter((option, index) => {
      const optionMatch = normalizeForMatch(option, caseInsensitive).includes(normalizedInput);
      if (optionMatch) {
        return true;
      }
      const label = formattedOptions[index];
      return normalizeForMatch(label, caseInsensitive).includes(normalizedInput);
    });
  }, [caseInsensitive, formattedOptions, options, trimmedInput]);

  const prioritizedOptions = useMemo<readonly string[]>(() => {
    if (trimmedInput === '') {
      return filteredOptions;
    }
    const canonical = getCanonicalValue(trimmedInput);
    if (!canonical) {
      return filteredOptions;
    }
    const canonicalIndex = filteredOptions.findIndex(option => option === canonical);
    if (canonicalIndex <= 0) {
      return filteredOptions;
    }
    return [canonical, ...filteredOptions.slice(0, canonicalIndex), ...filteredOptions.slice(canonicalIndex + 1)];
  }, [filteredOptions, getCanonicalValue, trimmedInput]);

  const showCreateItem = useMemo(() => {
    if (!specialCreateLabel) {
      return false;
    }
    if (trimmedInput === '') {
      return false;
    }
    const canonical = getCanonicalValue(trimmedInput);
    return canonical == null;
  }, [getCanonicalValue, specialCreateLabel, trimmedInput]);

  const listItems = useMemo<ListItem[]>(() => {
    const optionItems: ListItem[] = prioritizedOptions.map(option => ({
      kind: 'option' as const,
      value: option,
      label: formatOption ? formatOption(option, rowData) || option : option,
    }));

    if (!showCreateItem) {
      return optionItems;
    }

    const createValue = trimmedInput;
    const createLabel = specialCreateLabel ? specialCreateLabel(createValue) : createValue;
    return [
      {
        kind: 'create' as const,
        value: createValue,
        label: createLabel,
      },
      ...optionItems,
    ];
  }, [formatOption, prioritizedOptions, rowData, showCreateItem, specialCreateLabel, trimmedInput]);

  const createListItem = showCreateItem ? listItems[0] ?? null : null;
  const optionListItems = showCreateItem ? listItems.slice(1) : listItems;

  const implicitActiveIndex = useMemo(() => {
    if (showCreateItem || trimmedInput === '') {
      return null;
    }
    const canonical = getCanonicalValue(trimmedInput);
    if (!canonical) {
      return null;
    }
    const matchIndex = prioritizedOptions.findIndex(option => option === canonical);
    return matchIndex >= 0 ? matchIndex : null;
  }, [getCanonicalValue, prioritizedOptions, showCreateItem, trimmedInput]);

  const resolvedActiveIndex = activeIndex >= 0 ? activeIndex : implicitActiveIndex ?? -1;

  useEffect(() => {
    if (activeIndex === -1) {
      return;
    }
    if (activeIndex >= listItems.length) {
      setActiveIndex(listItems.length - 1);
    }
  }, [activeIndex, listItems.length]);

  useEffect(() => {
    if (activeIndex < 0 || !shouldAutoScrollRef.current) {
      return;
    }
    const listElement = listRef.current;
    if (!listElement) {
      return;
    }
    const itemElement = listElement.querySelector<HTMLDivElement>(`[data-item-index="${activeIndex}"]`);
    if (!itemElement) {
      return;
    }
    const itemTop = itemElement.offsetTop;
    const itemBottom = itemTop + itemElement.offsetHeight;
    const viewTop = listElement.scrollTop;
    const viewBottom = viewTop + listElement.clientHeight;
    // Add small epsilon to avoid 1px oscillations that kick off cascades
    const EPS = 2;
    if (itemTop < viewTop + EPS) {
      listElement.scrollTop = itemTop;
    } else if (itemBottom > viewBottom - EPS) {
      listElement.scrollTop = itemBottom - listElement.clientHeight;
    }
    // Only scroll once per keyboard step
    shouldAutoScrollRef.current = false;
  }, [activeIndex]);

  const finalizeValue = useCallback(
    (raw: string) => {
      const canonical = getCanonicalValue(raw) ?? raw;
      const committed = onCommit ? onCommit(canonical) : canonical;
      return committed;
    },
    [getCanonicalValue, onCommit],
  );

  const commitEditing = useCallback(
    (value: string) => {
      interactedRef.current = true;
      const finalValue = finalizeValue(value);
      committedValueRef.current = finalValue;
      const bridgedValue = parseValue(finalValue);
      onValueChange(bridgedValue);
      stopEditing();
    },
    [finalizeValue, onValueChange, parseValue, stopEditing],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        event.stopPropagation();
        interactedRef.current = true;
        shouldAutoScrollRef.current = true;
        setActiveIndex(prev => {
          if (listItems.length === 0) {
            return -1;
          }
          if (prev === -1) {
            return 0;
          }
          return Math.min(listItems.length - 1, prev + 1);
        });
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        event.stopPropagation();
        interactedRef.current = true;
        shouldAutoScrollRef.current = true;
        setActiveIndex(prev => {
          if (listItems.length === 0) {
            return -1;
          }
          if (prev <= 0) {
            return 0;
          }
          return prev - 1;
        });
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        interactedRef.current = true;
        if (activeIndex >= 0 && activeIndex < listItems.length) {
          commitEditing(listItems[activeIndex].value);
        } else {
          commitEditing(inputValue);
        }
        const context = params.context as SampleSheetGridContext;
        const direction = event.shiftKey ? -1 : 1;
        const columnId = params.column.getColId();
        const editorRowIndex =
          typeof params.rowIndex === 'number' ? params.rowIndex : params.node.rowIndex ?? null;
        queueMicrotask(() => {
          context.sampleSheet?.moveSelectionAfterEdit?.(columnId, editorRowIndex, direction);
        });
        return;
      }
      if (event.key === 'Tab') {
        event.preventDefault();
        event.stopPropagation();
        interactedRef.current = true;
        commitEditing(activeIndex >= 0 && activeIndex < listItems.length ? listItems[activeIndex].value : inputValue);
        api.tabToNextCell();
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        stopEditing(true);
        return;
      }
      if (event.key.length === 1 || event.key === 'Backspace' || event.key === 'Delete') {
        interactedRef.current = true;
        onKeyDown(event.nativeEvent);
        return;
      }
      onKeyDown(event.nativeEvent);
    },
    [activeIndex, api, commitEditing, inputValue, listItems, onKeyDown, stopEditing],
  );

  const handleItemMouseDown = useCallback(
    (value: string) => (event: MouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      interactedRef.current = true;
      commitEditing(value);
    },
    [commitEditing],
  );
  const handleItemMouseEnter = useCallback((index: number) => {
    // Hover should not trigger auto-scroll; it causes cascading jumps.
    shouldAutoScrollRef.current = false;
    setActiveIndex(index);
  }, []);

  const renderListItem = useCallback(
    (item: ListItem, index: number, activeIndexOverride: number) => (
      <div
        key={`${item.kind}-${item.value}-${index}`}
        data-item-index={index}
        role="button"
        className={`free-text-dropdown-editor__item${index === activeIndexOverride ? ' free-text-dropdown-editor__item--active' : ''}${item.kind === 'create' ? ' free-text-dropdown-editor__item--create' : ''}`}
        onMouseDown={handleItemMouseDown(item.value)}
        onMouseEnter={() => {
          handleItemMouseEnter(index);
        }}
      >
        {item.label}
      </div>
    ),
    [handleItemMouseDown, handleItemMouseEnter],
  );

  useGridCellEditor(
    useMemo(
      () => ({
        // isPopup: () => true,
        isCancelAfterEnd: () => {
          const noInteraction = !interactedRef.current;
          const nothingCommitted = committedValueRef.current == null;
          const unchangedFromStart = latestInputValueRef.current === initialInputRef.current;
          return noInteraction && nothingCommitted && unchangedFromStart;
        },
      }),
      [],
    ),
  );

  return (
    <div className="free-text-dropdown-editor">
      <input
        ref={inputRef}
        className="free-text-dropdown-editor__input"
        value={inputValue}
        placeholder={placeholder}
        onChange={event => {
          const nextValue = event.target.value;
          setInputValue(nextValue);
          setActiveIndex(-1);
          interactedRef.current = true;
          onValueChange(parseValue(nextValue));
        }}
        onKeyDownCapture={handleKeyDown}
        autoComplete="off"
      />
      <div ref={listRef} className="free-text-dropdown-editor__list">
        {createListItem && createSectionLabel ? (
          <div className="free-text-dropdown-editor__section-label">{createSectionLabel}</div>
        ) : null}
        {createListItem ? renderListItem(createListItem, 0, resolvedActiveIndex) : null}
        {existingSectionLabel ? (
          <div className="free-text-dropdown-editor__section-label free-text-dropdown-editor__section-label--secondary">
            {existingSectionLabel}
          </div>
        ) : null}
        {optionListItems.map((item, optionIndex) => {
          const actualIndex = showCreateItem ? optionIndex + 1 : optionIndex;
          return renderListItem(item, actualIndex, resolvedActiveIndex);
        })}
        {prioritizedOptions.length === 0 ? <div className="free-text-dropdown-editor__empty">No matches</div> : null}
      </div>
      <style>
        {`
          .free-text-dropdown-editor {
            display: flex;
            flex-direction: column;
            width: 100%;
            min-width: 180px;
            max-height: 240px;
            box-sizing: border-box;
            background: var(--chakra-colors-white, #fff);
            border: 1px solid rgba(0, 0, 0, 0.15);
            border-radius: 4px;
            box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
          }
          .free-text-dropdown-editor__input {
            border: none;
            outline: none;
            padding: 8px;
            font-size: 14px;
            border-bottom: 1px solid rgba(0, 0, 0, 0.1);
            box-sizing: border-box;
            width: 100%;
          }
          .free-text-dropdown-editor__list {
            overflow-y: auto;
            padding: 4px 0;
            flex: 1;
          }
          .free-text-dropdown-editor__section-label {
            padding: 6px 12px 2px;
            font-size: 11px;
            font-weight: 600;
            text-transform: uppercase;
            color: rgba(0, 0, 0, 0.65);
            letter-spacing: 0.04em;
          }
          .free-text-dropdown-editor__section-label--secondary {
            margin-top: 8px;
          }
          .free-text-dropdown-editor__item {
            padding: 6px 12px;
            cursor: pointer;
            user-select: none;
          }
          .free-text-dropdown-editor__item--active {
            background: rgba(5, 115, 230, 0.14);
          }
          .free-text-dropdown-editor__item--create {
            font-weight: 600;
          }
          .free-text-dropdown-editor__empty {
            padding: 8px 12px;
            color: rgba(0, 0, 0, 0.5);
            font-size: 12px;
          }
        `}
      </style>
    </div>
  );
}

FreeTextDropdownEditor.displayName = 'FreeTextDropdownEditor';

export default FreeTextDropdownEditor;
